import React, {useCallback, useRef, useState} from 'react';
import {ScrollView, Switch, View} from 'react-native';
import {useFocusEffect} from '@react-navigation/native';
import {makeStyles} from '@rn-vui/themed';
import {observer} from 'mobx-react';

import Text from '../../../components/themed/Text';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import {AppRoute} from '../../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../../models/navigationTypes';
import apiService from '../../../transport/apiService';
import {logger} from '../../../utils/logger';

type CommunicationsPreferences = {
  emailEnabled: boolean;
  smsEnabled: boolean;
};

/**
 * DIS-1586: further toggles made inside this window join the same write, so a
 * member flipping both switches produces one full-state POST instead of two
 * concurrent ones whose arrival order decides which consent change survives.
 */
const WRITE_COALESCE_MS = 600;

const AccountCommunicationsPreferencesScreen = observer(
  ({
    navigation: _navigation,
    route: _route,
  }: RouteNavigatorScreenProps<AppRoute.ACCOUNT_COMMUNICATIONS_PREFERENCES>) => {
    const styles = useStyles();

    // Null until a read confirms the member's stored preferences. A write built
    // on an unconfirmed value would send a consent state the member never chose
    // for the channel they did not touch (DIS-1586).
    const [preferences, setPreferences] = useState<CommunicationsPreferences | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Latest intent, written synchronously on every toggle so consecutive
    // toggles chain off each other rather than each snapshotting the other
    // channel from a render that is already stale.
    const pendingRef = useRef<CommunicationsPreferences | null>(null);
    // Last state the server confirmed, so a rejected write can roll back to it.
    const confirmedRef = useRef<CommunicationsPreferences | null>(null);
    const writeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const applyRead = useCallback((data: {Email_Opt_Out?: boolean; SMS_Opt_Out?: boolean}) => {
      const confirmed = {
        emailEnabled: !data.Email_Opt_Out,
        smsEnabled: !data.SMS_Opt_Out,
      };
      confirmedRef.current = confirmed;
      setPreferences(confirmed);
    }, []);

    const flushWrite = useCallback(async () => {
      const next = pendingRef.current;
      if (!next) {
        return;
      }
      pendingRef.current = null;
      setSaving(true);

      try {
        await apiService.updateCommunicationsPreferences({
          emailOptOut: !next.emailEnabled,
          smsOptOut: !next.smsEnabled,
        });
        // Take the post-write state from the server rather than trusting the
        // optimistic value.
        const response = await apiService.getCommunicationsPreferences();
        if (response?.data) {
          applyRead(response.data);
        }
      } catch (error) {
        logger.error(
          'AccountCommunicationsPreferencesScreen',
          'Failed to update communication preferences',
          {error},
        );
        setPreferences(confirmedRef.current);
      } finally {
        setSaving(false);
      }
    }, [applyRead]);

    useFocusEffect(
      useCallback(() => {
        let isActive = true;

        const fetchPreferences = async () => {
          try {
            setLoading(true);
            const response = await apiService.getCommunicationsPreferences();
            if (response?.data && isActive) {
              applyRead(response.data);
            }
          } catch (error) {
            logger.error(
              'AccountCommunicationsPreferencesScreen',
              'Failed to fetch communication preferences',
              {error},
            );
          } finally {
            if (isActive) {
              setLoading(false);
            }
          }
        };

        fetchPreferences();

        // Cleanup function to set isActive to false when the screen is unfocused
        return () => {
          isActive = false;
          // Never drop a coalesced change because the member navigated away
          // inside the window — send it now instead.
          if (writeTimerRef.current) {
            clearTimeout(writeTimerRef.current);
            writeTimerRef.current = null;
            flushWrite();
          }
        };
      }, [applyRead, flushWrite]),
    );

    const togglePreference = (key: keyof CommunicationsPreferences) => {
      const base = pendingRef.current ?? preferences;
      if (!base || saving) {
        return;
      }

      const next = {...base, [key]: !base[key]};
      pendingRef.current = next;
      setPreferences(next);

      if (writeTimerRef.current) {
        clearTimeout(writeTimerRef.current);
      }
      writeTimerRef.current = setTimeout(() => {
        writeTimerRef.current = null;
        flushWrite();
      }, WRITE_COALESCE_MS);
    };

    const toggleEmailSwitch = () => togglePreference('emailEnabled');
    const toggleSmsSwitch = () => togglePreference('smsEnabled');

    // The fallbacks preserve the pre-DIS-1586 appearance when the read failed.
    // Surfacing that failure is a separate concern (Zoho contact_not_found);
    // what DIS-1586 changes is that these unconfirmed values can no longer be
    // written back as though the member had chosen them.
    const emailEnabled = preferences?.emailEnabled ?? false;
    const smsEnabled = preferences?.smsEnabled ?? true;
    const switchesDisabled = saving || preferences === null;

    if (loading) {
      return (
        <ContentView>
          <Text>Loading preferences...</Text>
        </ContentView>
      );
    }

    return (
      <ScrollView>
        <ContentView testID="account-communications-preferences-screen">
          <View style={styles.headingContainer}>
            <Text noMargin variant="heading1">
              MARKETING
            </Text>
          </View>

          {/* Email Preference Row */}
          <View style={styles.preferenceContainer}>
            <View style={styles.textContainer}>
              <View style={styles.titleRow}>
                <Text style={styles.preferenceTitle}>Email Marketing</Text>
                <Text
                  style={[
                    styles.statusIndicator,
                    emailEnabled ? styles.statusOn : styles.statusOff,
                  ]}>
                  {emailEnabled ? 'ON' : 'OFF'}
                </Text>
              </View>
              <Text style={styles.preferenceDescription}>
                Receive promotional emails, special offers, and new product updates
              </Text>
            </View>
            <Switch
              trackColor={{false: '#767577', true: '#0054A6'}}
              ios_backgroundColor="#3e3e3e"
              onValueChange={toggleEmailSwitch}
              disabled={switchesDisabled}
              value={emailEnabled}
            />
          </View>

          {/* SMS Preference Row */}
          <View style={styles.preferenceContainer}>
            <View style={styles.textContainer}>
              <View style={styles.titleRow}>
                <Text style={styles.preferenceTitle}>SMS Marketing</Text>
                <Text
                  style={[styles.statusIndicator, smsEnabled ? styles.statusOn : styles.statusOff]}>
                  {smsEnabled ? 'ON' : 'OFF'}
                </Text>
              </View>
              <Text style={styles.preferenceDescription}>
                Receive SMS updates about promotions, exclusive deals, and member benefits
              </Text>
            </View>
            <Switch
              trackColor={{false: '#767577', true: '#0054A6'}}
              ios_backgroundColor="#3e3e3e"
              onValueChange={toggleSmsSwitch}
              disabled={switchesDisabled}
              value={smsEnabled}
            />
          </View>
        </ContentView>
      </ScrollView>
    );
  },
);

const useStyles = makeStyles(() => ({
  headingContainer: {
    marginBottom: 16,
  },
  preferenceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  textContainer: {
    flex: 1,
    marginRight: 12,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  preferenceTitle: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  statusIndicator: {
    fontSize: 12,
    fontWeight: 'bold',
    marginLeft: 8,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    overflow: 'hidden',
  },
  statusOn: {
    color: '#0054A6',
    backgroundColor: '#E6F0FA',
  },
  statusOff: {
    color: '#767577',
    backgroundColor: '#F0F0F0',
  },
  preferenceDescription: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 18,
    textAlign: 'left',
    color: 'grey',
  },
}));

export default AccountCommunicationsPreferencesScreen;
